import Link from "next/link";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { truncateWords } from "@/lib/utils/string";
import { buildUrl } from "@/lib/utils/url";
import type { Blog, Card as CardT } from "@/payload-types";
import type { CardVariant } from "@/components/frontend/ui/card";
import type { BadgeVariant } from "@/components/frontend/ui/badge/badge";
import { Card, CardContent, CardFigure, CardHeader } from "@/components/frontend/ui/card";
import { Button } from "@/components/frontend/ui/button";
import { Image } from "@/components/frontend/ui/image";
import { BlogInfo } from "./parts/info";

export type BlogSummaryCardProps = {
	post: Partial<Blog>;
	urlBase: string;
	variant?: CardVariant;
	className?: string;
	badgeVariant?: BadgeVariant;
	cardSettings?: CardT["settings"];
};

export function BlogSummaryCard({ post, urlBase, variant, className, badgeVariant, cardSettings }: BlogSummaryCardProps) {
	const t = useTranslations("blog");

	return (
		<article className="relative h-full">
			<Link href={buildUrl(urlBase, post.slug ?? "")} className="h-full">
				<Card variant={variant} className={cn("h-full", className)}>
					{post.meta?.image && (
						<CardFigure wrap={cardSettings?.image?.wrap ?? undefined}>
							<Image media={post.meta.image} />
						</CardFigure>
					)}
					<div className="flex flex-col grow">
						<CardContent>
							<BlogInfo post={post} badgeVariant={badgeVariant} />
							<p className="line-clamp-2 h4">{post.title}</p>
							{post.excerpt && <p>{truncateWords(post.excerpt, 16)}</p>}
							<div className="mt-auto">
								<Button variant="default" title={t("readMore")}>
									{t("readMore")}
								</Button>
							</div>
						</CardContent>
					</div>
				</Card>
			</Link>
		</article>
	);
}
